// packages/types/src/uc/categories.ts
// UC (United Classifieds / nehnutelnosti.sk) taxonómia kategórií.
// Zdroj ID: import docs counters/advertisement (2026-08-21). Bijekcia strážená contract testom.

export const PROPERTY_GROUPS = [
  'GARAGES_AND_PARKING', 'APARTMENTS', 'HOUSES', 'LANDS', 'SPACES', 'OBJECTS',
] as const
export type PropertyGroup = (typeof PROPERTY_GROUPS)[number]

export interface SubtypeDef {
  ucId: number
  group: PropertyGroup
  label: string
  /** UC ho už nepoužíva — v UI neponúkať, v tabuľke držať kvôli bijekcii. */
  deprecated?: true
}

export const PROPERTY_SUBTYPES = {
  // Kľúč zámerne zhodný s názvom skupiny — UC má pre GARAGES_AND_PARKING jediný podtyp, nejde o copy-paste chybu.
  GARAGES_AND_PARKING:       { ucId: 8,     group: 'GARAGES_AND_PARKING', label: 'Garáže a parkovacie miesta' },

  STUDIO_APARTMENT:          { ucId: 9,     group: 'APARTMENTS', label: 'Garsónka' },
  DOUBLE_STUDIO_APARTMENT:   { ucId: 10,    group: 'APARTMENTS', label: 'Dvojgarsónka' },
  ONE_ROOM_APARTMENT:        { ucId: 11,    group: 'APARTMENTS', label: '1-izbový byt' },
  TWO_ROOM_APARTMENT:        { ucId: 12,    group: 'APARTMENTS', label: '2-izbový byt' },
  THREE_ROOM_APARTMENT:      { ucId: 13,    group: 'APARTMENTS', label: '3-izbový byt' },
  FOUR_ROOM_APARTMENT:       { ucId: 14,    group: 'APARTMENTS', label: '4-izbový byt' },
  FIVE_PLUS_ROOM_APARTMENT:  { ucId: 15,    group: 'APARTMENTS', label: '5 a viac izbový byt' },
  HOLIDAY_APARTMENT:         { ucId: 16,    group: 'APARTMENTS', label: 'Apartmán' },
  LOFT:                      { ucId: 17,    group: 'APARTMENTS', label: 'Loft' },
  MAISONETTE:                { ucId: 18,    group: 'APARTMENTS', label: 'Mezonet' },

  FAMILY_HOUSE:              { ucId: 20,    group: 'HOUSES', label: 'Rodinný dom' },
  COUNTRY_HOUSE:             { ucId: 21,    group: 'HOUSES', label: 'Vidiecky dom' },
  FARM_SETTLEMENT:           { ucId: 22,    group: 'HOUSES', label: 'Poľnohospodárska usadlosť' },
  MOBILE_HOUSE:              { ucId: 23,    group: 'HOUSES', label: 'Mobilný dom' },
  HOUSEBOAT:                 { ucId: 24,    group: 'HOUSES', label: 'Hausbót' },
  CABIN_LOG_CABIN:           { ucId: 26,    group: 'HOUSES', label: 'Zrub' },
  COTTAGE_RECREATION_HOUSE:  { ucId: 27,    group: 'HOUSES', label: 'Chata, rekreačný domček' },
  GARDEN_HUT:                { ucId: 28,    group: 'HOUSES', label: 'Záhradný domček' },

  LAND_FOR_FAMILY_HOUSE:     { ucId: 30,    group: 'LANDS', label: 'Pozemok pre rodinný dom' },
  RECREATIONAL_LAND:         { ucId: 31,    group: 'LANDS', label: 'Rekreačný pozemok' },
  LAND_FOR_HOUSING:          { ucId: 32,    group: 'LANDS', label: 'Pozemok pre bytovú výstavbu' },
  LAND_FOR_CIVIC_AMENITIES:  { ucId: 33,    group: 'LANDS', label: 'Pozemok pre občiansku vybavenosť' },
  COMMERCIAL_ZONE:           { ucId: 34,    group: 'LANDS', label: 'Komerčná zóna' },
  INDUSTRIAL_ZONE:           { ucId: 35,    group: 'LANDS', label: 'Priemyselná zóna' },
  MIXED_ZONE:                { ucId: 36,    group: 'LANDS', label: 'Zmiešaná zóna' },
  GARDEN:                    { ucId: 37,    group: 'LANDS', label: 'Záhrada' },
  ORCHARD:                   { ucId: 38,    group: 'LANDS', label: 'Ovocný sad' },
  VINEYARD_HOP_GARDEN:       { ucId: 39,    group: 'LANDS', label: 'Vinica, chmeľnica' },
  MEADOW_PASTURE:            { ucId: 40,    group: 'LANDS', label: 'Lúka, pasienok' },
  ARABLE_LAND:               { ucId: 41,    group: 'LANDS', label: 'Orná pôda' },
  FOREST_LAND:               { ucId: 42,    group: 'LANDS', label: 'Lesný pozemok' },
  POND_LAKE:                 { ucId: 43,    group: 'LANDS', label: 'Rybník, vodná plocha' },
  LAND_FOR_ADVERTISING:      { ucId: 44,    group: 'LANDS', label: 'Pozemok pre reklamu' },
  CONSOLIDATED_GROUND:       { ucId: 78,    group: 'LANDS', label: 'Konsolidovaná pôda', deprecated: true },
  OTHER_LAND_TO_BUILDING:    { ucId: 30746, group: 'LANDS', label: 'Iný stavebný pozemok' },
  OTHER_AGRICULTURAL_LAND:   { ucId: 30747, group: 'LANDS', label: 'Iný poľnohospodársky pozemok' },

  OFFICES:                   { ucId: 46,    group: 'SPACES', label: 'Kancelárie' },
  BUSINESS_SPACES:           { ucId: 47,    group: 'SPACES', label: 'Obchodné priestory' },
  RESTAURANT_SPACES:         { ucId: 48,    group: 'SPACES', label: 'Reštauračné priestory' },
  STORAGE_AREAS:             { ucId: 49,    group: 'SPACES', label: 'Skladové priestory' },
  SPACE_FOR_PRODUCTION:      { ucId: 50,    group: 'SPACES', label: 'Výrobné priestory' },
  REPAIR_AREA:               { ucId: 51,    group: 'SPACES', label: 'Servisné priestory' },
  SPORTS_FACILITIES:         { ucId: 52,    group: 'SPACES', label: 'Športové priestory' },
  SPACE_FOR_ADVERTISEMENT:   { ucId: 53,    group: 'SPACES', label: 'Priestor pre reklamu' },

  APARTMENT_HOUSE:           { ucId: 55,    group: 'OBJECTS', label: 'Bytový dom' },
  RENTAL_HOUSE:              { ucId: 56,    group: 'OBJECTS', label: 'Nájomný dom' },
  OFFICE_BUILDING:           { ucId: 57,    group: 'OBJECTS', label: 'Administratívna budova' },
  COMMERCIAL_OBJECT:         { ucId: 58,    group: 'OBJECTS', label: 'Objekt pre obchod' },
  RESTAURANT:                { ucId: 59,    group: 'OBJECTS', label: 'Reštaurácia' },
  POLYFUNCTIONAL_BUILDING:   { ucId: 60,    group: 'OBJECTS', label: 'Polyfunkčný objekt' },
  WAREHOUSE_OBJECT:          { ucId: 61,    group: 'OBJECTS', label: 'Sklad' },
  MANUFACTURING_FACILITY:    { ucId: 62,    group: 'OBJECTS', label: 'Výrobný objekt' },
  REPAIR_FACILITY:           { ucId: 63,    group: 'OBJECTS', label: 'Opravárenský objekt' },
  OBJECT_FOR_BREEDING:       { ucId: 64,    group: 'OBJECTS', label: 'Objekt pre chov zvierat' },
  HOTEL_PENSION:             { ucId: 65,    group: 'OBJECTS', label: 'Hotel, penzión' },
  SPA:                       { ucId: 66,    group: 'OBJECTS', label: 'Kúpeľný objekt' },
  HISTORICAL_OBJECT:         { ucId: 67,    group: 'OBJECTS', label: 'Historický objekt' },
  COMMERCIAL_FACILITY:       { ucId: 68,    group: 'OBJECTS', label: 'Prevádzkový areál' },
  SPORT_OBJECT:              { ucId: 69,    group: 'OBJECTS', label: 'Športový objekt' },
  SMALL_POWER_STATION:       { ucId: 70,    group: 'OBJECTS', label: 'Malá elektráreň' },
  GAS_STATION:               { ucId: 71,    group: 'OBJECTS', label: 'Čerpacia stanica' },
  MOBILE_CELLS_AND_STANDS:   { ucId: 72,    group: 'OBJECTS', label: 'Unimobunky a stánky' },
} as const satisfies Record<string, SubtypeDef>

export type PropertySubtype = keyof typeof PROPERTY_SUBTYPES

export const PROPERTY_SUBTYPE_VALUES = Object.keys(PROPERTY_SUBTYPES) as readonly PropertySubtype[]

export const PROPERTY_SUBTYPE_DEFS: Record<PropertySubtype, SubtypeDef> = PROPERTY_SUBTYPES

export function subtypeGroup(subtype: PropertySubtype): PropertyGroup {
  return PROPERTY_SUBTYPE_DEFS[subtype].group
}

export function subtypeUcId(subtype: PropertySubtype): number {
  return PROPERTY_SUBTYPE_DEFS[subtype].ucId
}

export function subtypesForGroup(group: PropertyGroup, opts?: { includeDeprecated?: boolean }): PropertySubtype[] {
  return PROPERTY_SUBTYPE_VALUES.filter(
    (s) => PROPERTY_SUBTYPE_DEFS[s].group === group && (opts?.includeDeprecated || !PROPERTY_SUBTYPE_DEFS[s].deprecated),
  )
}

/**
 * Podtypy pre viac skupín naraz (Druh nehnuteľnosti v záujme klienta, kde je
 * Typ multiselect) — v poradí PROPERTY_GROUPS, nie v poradí výberu, aby bola
 * ponuka stabilná. Neznáme/prázdne skupiny sa ignorujú.
 */
export function subtypesForGroups(groups: readonly string[], opts?: { includeDeprecated?: boolean }): PropertySubtype[] {
  return PROPERTY_GROUPS.filter((g) => groups.includes(g)).flatMap((g) => subtypesForGroup(g, opts))
}

/**
 * Ponechá len podtypy patriace do niektorej zo skupín (neznáme kľúče vypadnú).
 * Formuláre ňou orezávajú Druh po zmene Typu; API rovnakú podmienku vynucuje
 * (validatePropertySubtypes) namiesto tichého orezania.
 */
export function filterSubtypesByGroups(subtypes: readonly string[], groups: readonly string[]): PropertySubtype[] {
  return subtypes.filter(
    (s): s is PropertySubtype => s in PROPERTY_SUBTYPE_DEFS && groups.includes(PROPERTY_SUBTYPE_DEFS[s as PropertySubtype].group),
  )
}

/** Predvyplnenie podtypu bytu z počtu izieb (garsónka/mezonet/loft sa vyberá ručne). */
export function apartmentSubtypeFromRooms(roomsCount: number | null | undefined): PropertySubtype | null {
  if (roomsCount == null) return null
  if (roomsCount <= 0) return null
  if (roomsCount === 1) return 'ONE_ROOM_APARTMENT'
  if (roomsCount === 2) return 'TWO_ROOM_APARTMENT'
  if (roomsCount === 3) return 'THREE_ROOM_APARTMENT'
  if (roomsCount === 4) return 'FOUR_ROOM_APARTMENT'
  return 'FIVE_PLUS_ROOM_APARTMENT'
}

/**
 * Dispozícia zo staršieho poľa záujmu (InterestEntry.apartmentTypes) → bytový podtyp.
 * Len na prevod: mobil ≤ 1.1.41 a staré verejné formuláre ešte posielajú dispozíciu.
 */
export const LEGACY_APARTMENT_TYPE_SUBTYPE = {
  ONE_ROOM: 'ONE_ROOM_APARTMENT',
  TWO_ROOM: 'TWO_ROOM_APARTMENT',
  THREE_ROOM: 'THREE_ROOM_APARTMENT',
  FOUR_ROOM: 'FOUR_ROOM_APARTMENT',
  FIVE_PLUS_ROOM: 'FIVE_PLUS_ROOM_APARTMENT',
} as const satisfies Record<string, PropertySubtype>

// Staré hodnoty PropertyType (pred UC migráciou na 6 skupín, odstránené z DB
// enumu — pôvodný CREATE TYPE "PropertyType" v migrations/0_init) v bookmarknutých
// odkazoch/filtroch verejných webov (apps/web, apps/tenant-web, apps/broker-web) —
// mapuje ich na nové skupiny, aby staré linky nevracali 0 výsledkov. Zoznam je
// zámerne zmrazený (immutable history, presne 20 pôvodných hodnôt) — nedopĺňať
// nové kľúče. Zhodné so subtypeGroup CASE-om v 20260822121644_uc_taxonomy
// migration.sql (tam ELSE 'OBJECTS' pre týchto 6 hodnôt, tu explicitné kvôli
// uzavretej doméne).
const LEGACY_PROPERTY_TYPE_MAP: Record<string, PropertyGroup> = {
  APARTMENT: 'APARTMENTS', STUDIO: 'APARTMENTS',
  HOUSE: 'HOUSES', VILLA: 'HOUSES', COTTAGE: 'HOUSES', RECREATIONAL: 'HOUSES',
  LAND: 'LANDS', AGRICULTURAL: 'LANDS',
  OFFICE: 'SPACES', RETAIL: 'SPACES', RESTAURANT: 'SPACES', WAREHOUSE: 'SPACES',
  GARAGE: 'GARAGES_AND_PARKING', PARKING: 'GARAGES_AND_PARKING',
  HALL: 'OBJECTS', HOTEL: 'OBJECTS', DEV_PROJECT: 'OBJECTS',
  INVESTMENT: 'OBJECTS', COMMERCIAL: 'OBJECTS', OBJECT: 'OBJECTS',
}

const CURRENT_PROPERTY_GROUPS = new Set<string>(PROPERTY_GROUPS)

/**
 * Normalizuje propertyType z URL query/filtra na aktuálnu UC skupinu.
 * Hodnota už v novej sade prejde bez zmeny; rozpoznaná stará hodnota sa
 * premapuje; nerozpoznaná (vrátane prázdnej/undefined) vráti `undefined`,
 * aby volajúci filter jednoducho zahodil (žiadny umelý fallback na OBJECTS).
 */
export function normalizePropertyType(type: string | undefined): PropertyGroup | undefined {
  if (!type) return undefined
  if (CURRENT_PROPERTY_GROUPS.has(type)) return type as PropertyGroup
  return LEGACY_PROPERTY_TYPE_MAP[type]
}
